import { Grid, GridItem } from '@chakra-ui/react';
import { GPSCoordinatesRootProps } from '@components/ui/coordinatesInput/GPSCoordinates.slots';
import {
  GPSCoordinatesFieldContextProvider,
  useGPSCoordinatesFieldContext,
} from '@components/ui/coordinatesInput/GPSCoordinatesFieldContext';
import { useFormikContext } from 'formik';
import { forwardRef, useEffect, useState } from 'react';

import { Coordinates, CoordinatesUnit, CoordinatesUnitEnum, NullableCoordinates } from '@/types/commons/commons.types';

import { GPSCoordinates } from './namespace';

const InjectMouseCoordinates = ({ name }: Readonly<{ name: string }>) => {
  const { mouseSelectedPosition } = useGPSCoordinatesFieldContext();

  const { setFieldValue, setFieldTouched } = useFormikContext<unknown>();

  useEffect(() => {
    const updateCoordinates = async () => {
      if (mouseSelectedPosition?.latitude !== null && mouseSelectedPosition?.latitude !== undefined) {
        await setFieldValue(`${name}.latitude`, mouseSelectedPosition.latitude, true);
        await setFieldValue(`${name}.longitude`, mouseSelectedPosition.longitude, true);
        await setFieldTouched(name, true);
      }
    };

    updateCoordinates();
  }, [mouseSelectedPosition, name, setFieldTouched, setFieldValue]);

  return null;
};

export interface GPSCoordinatesProps extends GPSCoordinatesRootProps {
  name: string;
  coordinates?: Coordinates;
  mouseSelectedPosition?: NullableCoordinates;
  isSelectionEnabled?: boolean;
  setSelectionOn?: (active: boolean) => void;
}

type GPSCoordinatesFieldContentProps = Omit<GPSCoordinatesProps, 'mouseSelectedPosition'>;

const GPSCoordinatesFieldContent = forwardRef<HTMLDivElement, GPSCoordinatesFieldContentProps>(
  ({ name, ...rest }, ref) => {
    const [unit, setUnit] = useState<CoordinatesUnit | null>(CoordinatesUnitEnum.DECIMAL_DEGREES);
    const { mouseSelectedPosition } = useGPSCoordinatesFieldContext();
    return (
      <GPSCoordinates.Root ref={ref} {...rest} width="100%">
        <Grid
          templateAreas={{
            base: `
                "unit dummy positionPicker"
                "lat lat lat"
                "long long long"
              `,
            '4k': `"lat long unit positionPicker"`,
          }}
          templateColumns={{
            base: '1fr minmax(0, 1fr) 1fr',
            '4k': '5fr 5fr 1fr 1fr',
          }}
          gap={1}
          width="100%"
        >
          <GridItem area="unit">
            <GPSCoordinates.UnitSelector unit={unit} onUnitChange={(value) => setUnit(value)} />
          </GridItem>
          <GridItem area="dummy" display={{ '4k': 'none' }} />
          <GridItem area="positionPicker" justifySelf={{ base: 'end' }}>
            {mouseSelectedPosition ? (
              <>
                <InjectMouseCoordinates name={name} />
                <GPSCoordinates.PositionPicker />
              </>
            ) : null}
          </GridItem>
          <GridItem area="lat">
            <GPSCoordinates.Input type="latitude" name={name} unit={unit} />
          </GridItem>
          <GridItem area="long">
            <GPSCoordinates.Input type="longitude" name={name} unit={unit} />
          </GridItem>
        </Grid>
      </GPSCoordinates.Root>
    );
  },
);

export const GPSCoordinatesField = forwardRef<HTMLDivElement, GPSCoordinatesProps>(
  function GPSCoordinatesInput(props, ref) {
    const { name, coordinates, mouseSelectedPosition, isSelectionEnabled, setSelectionOn, ...rest } = props;

    return (
      <GPSCoordinatesFieldContextProvider
        coordinates={coordinates}
        mouseSelectedPosition={mouseSelectedPosition}
        isSelectionEnabled={isSelectionEnabled}
        setSelectionOn={setSelectionOn}
      >
        <GPSCoordinatesFieldContent ref={ref} name={name} {...rest} />
      </GPSCoordinatesFieldContextProvider>
    );
  },
);
